import { cacheManager } from "../../../utilities/cacheManager";
import { getAttrDisplayName } from "../ez-quote.helpers";
import {
  Attribute, Category, Condition, SelectedAttribute, Item, OptionType,
} from "../ez-quote.types";

type CurrentItemState = {
  category: Category | null;
  attributes: { [key: string]: SelectedAttribute };
  condition: Condition | null;
  parentCategoryName?: string;
  goldTypeName?: string;
  itemTypeLabel?: string;
};

type CategoryItemState = {
  [categoryId: number]: {
    attributes: { [key: string]: SelectedAttribute };
    condition: Condition | null;
  };
};

type ItemHandlerDeps = {
  attributes: Attribute[];
  currentItem: CurrentItemState;
  conditions: Condition[];
  items: Item[];
  maxItems: number;
  categoryItemState: CategoryItemState;
  selectedRootCategory: Category | null;
  currentAttributeIndex: number;
  goldParentCategory: Category | null;
  isEditing: boolean;
  setCurrentItem: (v: any) => void;
  setCurrentAttributeIndex: (v: any) => void;
  setCategoryItemState: (v: any) => void;
  setSelectedCondition: (v: string | null) => void;
  setItems: (v: any) => void;
  setDraftItem: (v: any) => void;
  setIsEditing: (v: boolean) => void;
  setModelOptions: (v: any[]) => void;
  setCurrentStep: (v: number) => void;
  setGoldParentCategory: (v: Category | null) => void;
  setSelectedGoldItemType: (v: OptionType | null) => void;
  setGoldCurrentAttributeIndex: (v: number) => void;
  setattributeError: (v: string | null) => void;
  setError: (v: string | null) => void;
  setAttributes: (v: Attribute[]) => void;
  setManufacturerOptions: (v: any[]) => void;
  setCategoryHistory: (v: Category[][]) => void;
  setSelectedCategoryParent: (v: Category | null) => void;
  setSelectedRootCategory: (v: Category | null) => void;
  fetchModels: (categoryId: number, manufacturerId: string) => void;
  fetchManufacturers: (categoryId: number) => void;
  fetchAttributes: (categoryId: number) => void;
  resetAndFetchCategories: (showLoader?: boolean) => void;
};

export function useItemHandlers({
  attributes, currentItem, conditions, items, maxItems,
  categoryItemState, selectedRootCategory, currentAttributeIndex,
  goldParentCategory, isEditing,
  setCurrentItem, setCurrentAttributeIndex, setCategoryItemState, setSelectedCondition,
  setItems, setDraftItem, setIsEditing, setModelOptions, setCurrentStep,
  setGoldParentCategory, setSelectedGoldItemType, setGoldCurrentAttributeIndex,
  setattributeError, setError, setAttributes, setManufacturerOptions,
  setCategoryHistory, setSelectedCategoryParent, setSelectedRootCategory,
  fetchModels, fetchManufacturers, fetchAttributes, resetAndFetchCategories,
}: ItemHandlerDeps) {
  const clearAttributeValues = (attributeName: string) => {
    setCurrentItem((prev: CurrentItemState) => {
      const updatedAttributes = {
        ...prev.attributes,
        [attributeName]: { id: "", name: "" },
      };
      const attributeKeys = Object.keys(updatedAttributes);
      const currentIndex = attributeKeys.indexOf(attributeName);
      const attributesToClear = attributeKeys.slice(currentIndex + 1);
      setCurrentAttributeIndex(currentIndex);
      attributesToClear.forEach((key) => {
        updatedAttributes[key] = { id: "", name: "" };
      });
      if (prev.category) {
        setCategoryItemState((map: CategoryItemState) => {
          const prevState = map[prev.category!.category_id] || { attributes: {}, condition: null };
          const newAttributes = { ...prevState.attributes, ...updatedAttributes };
          attributesToClear.concat(attributeName).forEach((key) => {
            newAttributes[key] = { id: "", name: "" };
          });
          return {
            ...map,
            [prev.category!.category_id]: { ...prevState, attributes: newAttributes },
          };
        });
      }
      return { ...prev, attributes: updatedAttributes };
    });
  };

  const handleAttributeSelect = (
    attributeName: string,
    selectedOption: SelectedAttribute
  ) => {
    const isTextArea = attributes.find((a) => a.coreAttributeName === attributeName)?.displayType === "TEXT_AREA";
    let formattedName = selectedOption.name;
    if (!isTextArea && selectedOption.name !== "Other/I'm not sure") {
      formattedName = selectedOption.name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }
    setCurrentItem((prev: CurrentItemState) => {
      const updated = {
        ...prev,
        attributes: { ...prev.attributes, [attributeName]: { ...selectedOption, name: formattedName } },
      };
      if (updated.category) {
        setCategoryItemState((map: CategoryItemState) => ({
          ...map,
          [updated.category!.category_id]: {
            attributes: updated.attributes,
            condition: updated.condition,
          },
        }));
      }
      return updated;
    });

    if (attributeName === "Manufacturer" && currentItem.category) {
      if (selectedOption.id) {
        fetchModels(currentItem.category.category_id, selectedOption.id);
        if (currentAttributeIndex === 0) setCurrentAttributeIndex(1);
      } else {
        setModelOptions([]);
        clearAttributeValues("Model Number");
      }
    } else if (attributeName === "Model Number") {
      if (selectedOption.id) {
        let attributeIndex = 2;
        const storageMatch = selectedOption.name.match(/(\d+)\s*(GB|MB|TB|KB)/i);
        if (storageMatch) {
          const storageCapacity = `${storageMatch[1]} ${storageMatch[2].toUpperCase()}`;
          if (currentItem?.category?.category_name !== "Tablets") {
            setCurrentItem((prev: CurrentItemState) => ({
              ...prev,
              attributes: { ...prev.attributes, "Internal Storage Capacity": { id: storageCapacity, name: storageCapacity } },
            }));
          } else {
            setCurrentItem((prev: CurrentItemState) => ({
              ...prev,
              attributes: { ...prev.attributes, "Internal Storage Capacity (GB)": { id: storageCapacity, name: storageCapacity } },
            }));
          }
          attributeIndex = 3;
        }
        setCurrentAttributeIndex(attributeIndex);
      }
    } else {
      setCurrentAttributeIndex((prev: number) => prev + 1);
    }
    setattributeError(null);
  };

  const handleConditionSelect = (conditionId: string) => {
    const condition = conditions.find((cond) => cond.id === conditionId) || null;
    setCurrentItem((prev: CurrentItemState) => {
      const updated = { ...prev, condition };
      if (updated.category) {
        setCategoryItemState((map: CategoryItemState) => ({
          ...map,
          [updated.category!.category_id]: {
            attributes: updated.attributes,
            condition: updated.condition,
          },
        }));
      }
      return updated;
    });
    setSelectedCondition(conditionId);
  };

  const handleEditItem = (item: Item) => {
    setIsEditing(true);
    setItems((prev: Item[]) => prev.filter((it) => it.id !== item.id));
    setCurrentItem({
      category: item.category,
      attributes: { ...item.attributes },
      condition: item.condition,
      parentCategoryName: item.parentCategoryName,
      goldTypeName: item.goldTypeName,
      itemTypeLabel: item.itemTypeLabel,
    });
    setSelectedCondition(item.condition ? item.condition.id : null);

    const isJewelryItem = !!item.parentCategoryName && !!item.goldParentCategory;
    if (isJewelryItem) {
      setCategoryItemState({
        [item.category.category_id]: {
          attributes: item.attributes,
          condition: item.condition,
        },
      });
      setGoldParentCategory(item.goldParentCategory!);
      const isOtherType = item.itemTypeLabel === "Other/I'm not sure";
      const hasOwnChildren = (item.goldParentCategory!.childCategories?.length ?? 0) > 0;
      const matchingChild = !isOtherType && hasOwnChildren
        ? item.goldParentCategory!.childCategories!.find(
            (c) => c.category_id === item.category.category_id
          )
        : null;
      setSelectedGoldItemType(
        isOtherType
          ? { key: "other", value: "Other/I'm not sure" }
          : hasOwnChildren
            ? (matchingChild
                ? { key: String(matchingChild.category_id), value: matchingChild.category_name }
                : { key: "other", value: "Other/I'm not sure" })
            : { key: String(item.category.category_id), value: item.itemTypeLabel ?? item.category.category_name }
      );
      setGoldCurrentAttributeIndex(0);
      setCurrentStep(2);
    } else {
      fetchAttributes(item.category.category_id);
      if (item.attributes["Manufacturer"]) {
        fetchManufacturers(item.category.category_id);
        if (item.attributes["Model Number"]) {
          fetchModels(item.category.category_id, item.attributes["Manufacturer"].id);
        }
      }
      const selectedCount = Object.keys(item.attributes).length;
      setCurrentAttributeIndex(selectedCount > 0 ? selectedCount - 1 : 0);
      setGoldCurrentAttributeIndex(0);
      setGoldParentCategory(null);
      setSelectedGoldItemType(null);
      setCurrentStep(3);
    }
  };

  const handleDeleteItem = (id: string) => {
    const updatedItems = items.filter((item) => item.id !== id);
    setItems(updatedItems);
    if (updatedItems.length === 0) {
      setGoldParentCategory(null);
      setSelectedGoldItemType(null);
      setCategoryHistory([]);
      setSelectedCategoryParent(null);
      setSelectedRootCategory(null);
      setGoldCurrentAttributeIndex(0);
      setAttributes([]);
      setCategoryItemState({});
      setCurrentItem({ category: null, attributes: {}, condition: null });
      resetAndFetchCategories();
      setCurrentStep(2);
    }
  };

  const handleAddItem = () => {
    if (items.length >= maxItems) return;
    setIsEditing(false);
    setSelectedCondition(null);
    setCurrentItem({ category: null, attributes: {}, condition: null });
    setAttributes([]);
    setManufacturerOptions([]);
    setModelOptions([]);
    setCurrentAttributeIndex(0);
    setGoldCurrentAttributeIndex(0);
    setGoldParentCategory(null);
    setSelectedGoldItemType(null);
    setCategoryItemState({});
    cacheManager.clearApplicationState();
    setCategoryHistory([]);
    setSelectedCategoryParent(null);
    setSelectedRootCategory(null);
    resetAndFetchCategories();
    setCurrentStep(2);
  };

  const addItemToList = () => {
    if (!currentItem.category || !currentItem.condition) {
      setError("Incomplete item details.");
      return;
    }
    const hasIncompleteInfo = Object.values(currentItem.attributes).some(
      (attr) => attr.name === "Other/I'm not sure"
    );
    const newItem: Item = {
      id: `${currentItem.category.category_id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      category: currentItem.category,
      attributes: { ...currentItem.attributes },
      condition: currentItem.condition,
      estimate: hasIncompleteInfo ? "N/A" : "",
      rootCategoryName: selectedRootCategory?.category_name,
    };
    setItems((prev: Item[]) => [...prev, newItem]);
    setDraftItem(newItem);
  };

  const handleContinueStep3 = () => {
    setattributeError(null);
    const incompleteAttributes = attributes.filter((attr) => {
      const selected = currentItem.attributes[attr.coreAttributeName];
      return !selected?.name || selected.id == null;
    });
    if (incompleteAttributes.length > 0) {
      incompleteAttributes.forEach((attr) => {
        setattributeError(
          `You must select a ${getAttrDisplayName(attr.coreAttributeName)} to continue`
        );
      });
      return false;
    }
    return true;
  };

  return {
    clearAttributeValues,
    handleAttributeSelect,
    handleConditionSelect,
    handleEditItem,
    handleDeleteItem,
    handleAddItem,
    addItemToList,
    handleContinueStep3,
  };
}
